// THUNKS/ACTIONS ==============================================================

import {
  MessageTemplateAdminDto,
  MessageTemplateCategoryAdminDto,
  MessageTemplateCategoryDto,
} from '@act/shared/data-transfer-objects';
import {
  createEntityAdapter,
  createSlice,
  createSelector,
  createAction,
  PayloadAction,
} from '@reduxjs/toolkit';
import { messageTemplatesRequests } from 'redux/requests/message-templates.requests';
import { useAppSelector } from 'redux/store-hooks';
import { MessageTemplatesState } from 'redux/types/messageTemplatesTypes';
import { createApiThunk } from 'redux/utils/api-thunk';

export const getMessageTemplates = createApiThunk(
  messageTemplatesRequests.getMessageTemplateCategories
);

// INITIAL STATE ===============================================================
const messageTemplatesEntityAdapter =
  createEntityAdapter<MessageTemplateCategoryDto>();

const initialState: MessageTemplatesState = {
  messageTemplates: messageTemplatesEntityAdapter.getInitialState({}),
  status: 'initial',
};

// THUNKS/ACTIONS ==============================================================

export const createMessageTemplateCategory = createApiThunk(
  messageTemplatesRequests.createMessageTemplateCategory
);

export const updateMessageTemplateCategory = createApiThunk(
  messageTemplatesRequests.updateMessageTemplateCategory
);

export const createMessageTemplate = createApiThunk(
  messageTemplatesRequests.createMessageTemplate
);

export const updateMessageTemplate = createApiThunk(
  messageTemplatesRequests.updateMessageTemplate
);

export const createMessageTemplateCategorySuccess =
  createAction<MessageTemplateCategoryAdminDto>(
    'messageTemplates/createMessageTemplateCategorySuccess'
  );

export const updateMessageTemplateCategorySuccess =
  createAction<MessageTemplateCategoryAdminDto>(
    'messageTemplates/updateMessageTemplateCategorySuccess'
  );

export const createMessageTemplateSuccess =
  createAction<MessageTemplateAdminDto>(
    'messageTemplates/createMessageTemplateSuccess'
  );

export const updateMessageTemplateSuccess =
  createAction<MessageTemplateAdminDto>(
    'messageTemplates/updateMessageTemplateSuccess'
  );
// SLICE =======================================================================
export const messageTemplatesSlice = createSlice({
  name: 'messageTemplates',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getMessageTemplates.pending, state => {
        state.status = 'loading';
        messageTemplatesEntityAdapter.removeAll(state.messageTemplates);
      })
      .addCase(getMessageTemplates.fulfilled, (state, action) => {
        state.status = 'success';
        messageTemplatesEntityAdapter.setAll(
          state.messageTemplates,
          action.payload
        );
      })
      .addCase(getMessageTemplates.rejected, state => {
        state.status = 'failed';
      });
    builder.addCase(
      createMessageTemplateCategorySuccess,
      (state, action: PayloadAction<MessageTemplateCategoryAdminDto>) => {
        state.status = 'success';
        messageTemplatesEntityAdapter.addOne(state.messageTemplates, {
          id: action.payload.id,
          name: action.payload.name,
          sortOrder: action.payload.sortOrder,
          stageKey: action.payload.stageKey,
          templates: [],
        });
      }
    );
    builder.addCase(
      updateMessageTemplateCategorySuccess,
      (state, action: PayloadAction<MessageTemplateCategoryAdminDto>) => {
        state.status = 'success';
        messageTemplatesEntityAdapter.upsertOne(state.messageTemplates, {
          id: action.payload.id,
          name: action.payload.name,
          sortOrder: action.payload.sortOrder,
          stageKey: action.payload.stageKey,
        } as MessageTemplateCategoryDto);
      }
    );
    builder.addCase(createMessageTemplateSuccess, (state, action) => {
      state.status = 'success';
      const category =
        state.messageTemplates.entities[action.payload.categoryId];
      if (category) {
        const newTemplate = {
          id: action.payload.id,
          template: action.payload.template,
          categoryId: action.payload.categoryId,
          sortOrder: action.payload.sortOrder,
          template_clients: action.payload.template_clients.map(clientId => ({
            clientId,
            templateId: action.payload.id,
            id: 0,
          })),
          allClients: action.payload.allClients,
        };

        messageTemplatesEntityAdapter.updateOne(state.messageTemplates, {
          id: action.payload.categoryId,
          changes: {
            templates: [...category.templates, newTemplate],
          },
        });
      }
    });
    builder.addCase(
      updateMessageTemplateSuccess,
      (state, action: PayloadAction<MessageTemplateAdminDto>) => {
        const category =
          state.messageTemplates.entities[action.payload.categoryId];
        if (category) {
          const updatedTemplate = {
            id: action.payload.id,
            template: action.payload.template,
            categoryId: action.payload.categoryId,
            sortOrder: action.payload.sortOrder,
            template_clients: action.payload.template_clients.map(clientId => ({
              clientId,
              templateId: action.payload.id,
              id: 0,
            })),
            allClients: action.payload.allClients,
          };

          const updatedTemplates = category.templates.map(template =>
            template.id === action.payload.id ? updatedTemplate : template
          );

          messageTemplatesEntityAdapter.updateOne(state.messageTemplates, {
            id: action.payload.categoryId,
            changes: {
              templates: updatedTemplates,
            },
          });
        }
        state.status = 'success';
      }
    );
  },
});

// REDUCER =====================================================================
export const messageTemplatesReducer = messageTemplatesSlice.reducer;

// SELECTORS ===================================================================
const selectMessageTemplatesSliceState = state =>
  (state as { messageTemplates: typeof initialState })?.messageTemplates;

export const selectAllMessageTemplates = createSelector(
  selectMessageTemplatesSliceState,
  messageTemplatesState => messageTemplatesState?.messageTemplates
);

export const messageTemplatesSelectors = {
  useSelectAll: () => useAppSelector(selectAllMessageTemplates),
};
